import { useTask } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useAuditEnricher,
    useFormLifeCycle,
} from "@/Shared";

import { TaskFormViewModel } from "../ViewModels";
import { TaskValidationSchema } from "../TaskFormValidation";
import { TaskModel } from "../TaskModel";


/**
 * useTaskForm hook – orchestrates task form initialization and submission.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Coordinates permissions, domain state and enrichment layers
 * - Builds and exposes the TaskFormViewModel to the UI
 * - Delegates submission logic and feedback handling to dedicated hooks
 * 
 * Design notes:
 * - This hook does not perform CRUD or permission resolution directly
 * - It acts as an orchestration layer between UI and domain-specific hooks
 */
export const useTaskForm = (props: DomainHookProps): DomainFormHook => {
    const { permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.Task;

    /**
     * Task domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const taskHook = useTask();

    const {
        enricherResponse,
        userMap,
        getEnrichedAudit,
    } = useAuditEnricher<TaskModel>();


    /**
     * Task-related enums exposed to the UI layer.
     * Used to build the form view model.
     */
    const { TaskViewEnums } = useAppEnum();

    /**
     * Aggregated response representing the global initialization state.
     * Combines:
     * - task domain state
     * - permission loading state
     * - enrichment layer state
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            taskHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: taskHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: taskHook,
        buildVM: (currentEntity: TaskModel) => {
            return new TaskFormViewModel(
                TaskViewEnums,
                getEnrichedAudit(currentEntity)
            )
        },
    });


    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: TaskViewEnums,
        ValidationSchema: TaskValidationSchema(TaskViewEnums),
    }
}